import { Fragment } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Box, Label, Typography } from '@pocketlaw/tetris';
import { useFormikContext } from 'formik';
import { useEsigningProviderMetadata } from 'app/domains/esigning/hooks/useEsigningProviderMetadata';
import { EsigningProviderAuthenticationModeSlug, EsigningRecipientType } from 'shared/domains/apollo/generated/types';
import { TextField } from 'shared/domains/forms';
import { PhoneNumberField } from 'shared/domains/forms/components/PhoneNumberField';
import { MessageField } from './MessageField';
import { RoleAndAuth } from './RoleAndAuth';
import { useEsigning } from '../../../EsigningProvider';
import { ColumnsContainer, ContainerDivider, FieldWrapper, LeftColumn, RightColumn } from './styled';
export function Content(props) {
    const { isEdit } = props;
    const { values: formValues } = useFormikContext();
    const { type, authenticationMode } = formValues;
    const { pending } = useEsigning();
    const { properties: metadataProperties } = useEsigningProviderMetadata();
    const { nameFormat, canUseTitleField, canUseEsigningMessages } = metadataProperties;
    const isSigning = type === EsigningRecipientType.Signatory;
    const showMessageField = canUseEsigningMessages;
    const showTitleField = isSigning && canUseTitleField;
    const showMobileField = isSigning && authenticationMode === EsigningProviderAuthenticationModeSlug.SmsOtp;
    const disableRoleField = isEdit && pending;
    const optionalFieldDescription = t({
        comment: 'Signing party form field - Optional field text',
        message: 'If applicable',
    });
    return (<Fragment>
      <ColumnsContainer>
        <LeftColumn>
          <div style={{ position: 'relative' }}>
            <Label>
              <Trans comment="Label for the add/edit e-signing party details">
                Add details for signing or non-signing parties
              </Trans>
            </Label>
          </div>
          <Typography $appearance="100" $fontSize="small">
            <Trans comment="Message for the add/edit e-signing party details">
              Signing parties will be able to sign the document. Non-signing parties will receive a copy once it&apos;s
              signed. You can also select and manage contacts under the Contact library tab.
            </Trans>
          </Typography>
        </LeftColumn>
        <RightColumn>
          {nameFormat === 'firstNameLastName' && (<FieldWrapper>
              <TextField name="firstName" label={t({
                comment: 'Signing party form field - First name',
                message: 'First name',
            })}/>
            </FieldWrapper>)}
          {nameFormat === 'firstNameLastName' && (<FieldWrapper>
              <TextField name="lastName" label={t({ comment: 'Signing party form field - Last name', message: 'Last name' })}/>
            </FieldWrapper>)}
          {nameFormat === 'fullName' && (<FieldWrapper>
              <TextField name="name" label={t({ comment: 'Signing party form field - Full name', message: 'Full name' })}/>
            </FieldWrapper>)}
          <FieldWrapper>
            <TextField name="email" label={t({ comment: 'Signing party form field - Email', message: 'Email' })}/>
          </FieldWrapper>
          {showTitleField && (<FieldWrapper>
              <TextField name="title" label={t({ comment: 'Signing party form field - Title', message: 'Title' })} description={optionalFieldDescription}/>
            </FieldWrapper>)}
          <FieldWrapper>
            <TextField name="label" label={t({ comment: 'Signing party form field - Company', message: 'Company' })} description={optionalFieldDescription}/>
          </FieldWrapper>
          <RoleAndAuth disableRole={disableRoleField}/>
          {showMobileField && (<PhoneNumberField stretch name="mobile" label={t({
                comment: 'Signing party form field - Phone number',
                message: 'Phone number',
            })}/>)}
        </RightColumn>
      </ColumnsContainer>

      {showMessageField && (<Fragment>
          <ContainerDivider />

          <Box display="block" pt="2xl">
            <ColumnsContainer>
              <LeftColumn>
                <Typography $appearance="300" $fontSize="large" $fontWeight="bold">
                  <Trans comment="Title for the add/edit e-signing party personal message">Personal message</Trans>
                </Typography>
                <Typography $appearance="100" $fontSize="small">
                  <Trans comment="Description for the add/edit e-signing party personal message">
                    Add a message that will only be visible to this specific party.
                  </Trans>
                </Typography>
              </LeftColumn>
              <RightColumn>
                <MessageField />
              </RightColumn>
            </ColumnsContainer>
          </Box>
        </Fragment>)}
    </Fragment>);
}
